"use client";

import { useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";

const buildoutCategories = [
  {
    label: "Buildout & Installation",
    href: "/services/buildout",
    links: [
      { href: "/services/structured-cabling", label: "Structured Cabling & Low-Voltage" },
      { href: "/services/server-room", label: "Server Room & Rack Buildout" },
      { href: "/services/wireless-installation", label: "Wireless & Network Installation" },
      { href: "/services/security-access", label: "Security & Access Control" },
      { href: "/services/conference-rooms", label: "Conference Rooms & Presentation Spaces" },
      { href: "/services/audio-systems", label: "Audio Systems" },
    ],
  },
];

const ephemeralCategories = [
  {
    label: "Ephemeral Infrastructure",
    href: "/services/ephemeral",
    links: [
      { href: "/services/event-network", label: "Event Network Design & Deployment" },
      { href: "/services/festival", label: "Festival & Large-Venue Connectivity" },
      { href: "/services/temporary-office", label: "Temporary Office Infrastructure" },
      { href: "/services/production", label: "Production & Broadcast Support" },
      { href: "/services/deploy-teardown", label: "Design, Deploy & Teardown" },
    ],
  },
];

const managementCategories = [
  {
    label: "Management Bundles",
    href: "/services/management/",
    links: [
      { href: "/services/management/core-suite", label: "Core Suite" },
      { href: "/services/management/essentials", label: "Management Essentials" },
    ],
  },
  {
    label: "Identity & Devices",
    href: "/services/management/identity/",
    links: [
      { href: "/services/management/identity", label: "Identity Management" },
      { href: "/services/management/endpoint-management", label: "Endpoint Management" },
    ],
  },
  {
    label: "Monitoring",
    href: "/services/management/network/",
    links: [
      { href: "/services/management/network", label: "Network Management" },
      { href: "/services/management/server", label: "Server Management" },
      { href: "/services/management/backup", label: "Backup Management" },
      { href: "/services/management/domain", label: "Domain Management" },
    ],
  },
  {
    label: "Security",
    href: "/services/management/awareness/",
    links: [
      { href: "/services/management/awareness", label: "Cybersecurity Awareness" },
    ],
  },
  {
    label: "Support",
    href: "/services/management/support/",
    links: [
      { href: "/services/management/support", label: "Managed Support" },
    ],
  },
];

const techConsultingCategories = [
  {
    label: "Strategy & Advisory",
    href: "/services/technology-consulting/strategy/",
    links: [
      { href: "/services/technology-consulting/initial-assessment", label: "Initial Assessment" },
      { href: "/services/technology-consulting/fractional-cto", label: "Fractional CTO" },
      { href: "/services/technology-consulting/disaster-recovery-plan-development", label: "Disaster Recovery Planning" },
    ],
  },
  {
    label: "AI & Automation",
    href: "/services/ai",
    links: [
      { href: "/services/technology-consulting/ai-enablement", label: "AI Enablement" },
      { href: "/services/technology-consulting/mcp-tooling", label: "MCP Tooling" },
      { href: "/services/technology-consulting/agent-governance", label: "Agent Governance & PAM" },
      { href: "/services/technology-consulting/audit-accountability", label: "Audit & Accountability" },
      { href: "/services/technology-consulting/vibe-coding", label: "Vibe Coding & Custom Development" },
    ],
  },
  {
    label: "Cloud",
    href: "/services/technology-consulting/cloud/",
    links: [
      { href: "/services/technology-consulting/azure-consulting-and-development", label: "Azure Consulting & Development" },
      { href: "/services/technology-consulting/cloud-storage-and-computing-solutions", label: "Cloud Storage & Computing" },
    ],
  },
  {
    label: "Identity & Security",
    href: "/services/technology-consulting/identity-security/",
    links: [
      { href: "/services/technology-consulting/jumpcloud-consulting", label: "JumpCloud Consulting" },
      { href: "/services/technology-consulting/okta-consulting", label: "Okta Consulting" },
      { href: "/services/technology-consulting/entra-and-intune-consulting", label: "Entra & Intune Consulting" },
      { href: "/services/technology-consulting/password-management", label: "Password Management" },
      { href: "/services/technology-consulting/cybersecurity-assessment", label: "Cybersecurity Assessment" },
    ],
  },
  {
    label: "Networking",
    href: "/services/technology-consulting/networking/",
    links: [
      { href: "/services/technology-consulting/network-engineering", label: "Network Design & Engineering" },
      { href: "/services/technology-consulting/firewall-and-network-security", label: "Firewall & Network Security" },
      { href: "/services/technology-consulting/wifi-assessment", label: "WiFi Assessment" },
    ],
  },
  {
    label: "Compute & Storage",
    href: "/services/technology-consulting/compute-storage/",
    links: [
      { href: "/services/technology-consulting/dell-poweredge-server-solutions", label: "Dell PowerEdge Server Solutions" },
      { href: "/services/technology-consulting/synology-storage-consulting", label: "Synology Storage Consulting" },
      { href: "/services/technology-consulting/vmware-administration-infrastructure-design", label: "VMware Administration" },
    ],
  },
];


const ourWorkLinks = [
  { href: "/industries", label: "Industries" },
  { href: "/case-studies", label: "Case Studies" },
  { href: "/our-work/clients", label: "Clients" },
];

const resourcesLinks = [
  { href: "/resources/foundations", label: "Foundations" },
  { href: "/resources/university", label: "University" },
  { href: "/resources/free-resources", label: "Free Resources" },
  { href: "/resources/glossary", label: "Glossary" },
];

const aboutLinks = [
  { href: "/about/leadership", label: "Leadership" },
  { href: "/about/our-story", label: "Our Story" },
  { href: "/careers", label: "Careers" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [mgmtOpen, setMgmtOpen] = useState(false);
  const [tcOpen, setTcOpen] = useState(false);
  const [ourWorkOpen, setOurWorkOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [activeDesktopDropdown, setActiveDesktopDropdown] = useState<string | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function openDesktopDropdown(name: string) {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setActiveDesktopDropdown(name);
  }

  function closeDesktopDropdown() {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => setActiveDesktopDropdown(null), 150);
  }

  return (
    <header className="site-nav fixed top-0 left-0 right-0 z-50 bg-[#F4F2EF]/85 backdrop-blur-sm [transform:translateZ(0)]">
      <div className="flex items-center py-1.5 sm:py-[5px] px-4 sm:px-6 border-b border-black/[0.08]">
        <Link href="/" className="flex-shrink-0" onClick={() => setOpen(false)}>
          <Image
            src="/assets/RS_Systems_Pulsar_Logo_cropped.svg"
            alt="RSystems"
            width={320}
            height={80}
            className="h-10 sm:h-[90px] w-auto"
            priority
          />
        </Link>

        <nav className="ml-auto hidden md:flex items-center gap-8">

          <div onMouseEnter={() => openDesktopDropdown('services')} onMouseLeave={closeDesktopDropdown}>
            <Link
              href="/services"
              onClick={() => setActiveDesktopDropdown(null)}
              className={`flex items-center gap-1 text-sm transition-colors ${
                activeDesktopDropdown === 'services' ? "text-[#1A1A1A]" : "text-[#1A1A1A]/50 hover:text-[#1A1A1A]"
              }`}
            >
              Services
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                aria-hidden="true"
                className="mt-px"
                style={{ transform: activeDesktopDropdown === 'services' ? "rotate(180deg)" : undefined, transition: "transform 0.15s" }}
              >
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>

          <div className="relative" onMouseEnter={() => openDesktopDropdown('ourwork')} onMouseLeave={closeDesktopDropdown}>
            <Link
              href="/our-work"
              onClick={() => setActiveDesktopDropdown(null)}
              className={`flex items-center gap-1 text-sm transition-colors ${
                activeDesktopDropdown === 'ourwork' ? "text-[#1A1A1A]" : "text-[#1A1A1A]/50 hover:text-[#1A1A1A]"
              }`}
            >
              Our Work
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" className="mt-px" style={{ transform: activeDesktopDropdown === 'ourwork' ? "rotate(180deg)" : undefined, transition: "transform 0.15s" }}>
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <div className={`absolute top-full left-0 z-50 pt-2 transition-opacity duration-150 ${activeDesktopDropdown === 'ourwork' ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
              <div className="bg-[#F4F2EF] border border-black/[0.08] rounded-lg shadow-lg py-1.5 min-w-[148px]">
                {ourWorkLinks.map((link) => (
                  <Link key={link.href} href={link.href} onClick={() => setActiveDesktopDropdown(null)} className="block px-4 py-2 text-sm text-[#1A1A1A]/60 hover:text-[#1A1A1A] hover:bg-black/[0.04] transition-colors">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link
            href="/pricing"
            className="text-sm text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors"
          >
            Pricing
          </Link>

          <div className="relative" onMouseEnter={() => openDesktopDropdown('resources')} onMouseLeave={closeDesktopDropdown}>
            <Link
              href="/resources"
              onClick={() => setActiveDesktopDropdown(null)}
              className={`flex items-center gap-1 text-sm transition-colors ${
                activeDesktopDropdown === 'resources' ? "text-[#1A1A1A]" : "text-[#1A1A1A]/50 hover:text-[#1A1A1A]"
              }`}
            >
              Resources
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" className="mt-px" style={{ transform: activeDesktopDropdown === 'resources' ? "rotate(180deg)" : undefined, transition: "transform 0.15s" }}>
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <div className={`absolute top-full left-0 z-50 pt-2 transition-opacity duration-150 ${activeDesktopDropdown === 'resources' ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
              <div className="bg-[#F4F2EF] border border-black/[0.08] rounded-lg shadow-lg py-1.5 min-w-[160px]">
                {resourcesLinks.map((link) => (
                  <Link key={link.href} href={link.href} onClick={() => setActiveDesktopDropdown(null)} className="block px-4 py-2 text-sm text-[#1A1A1A]/60 hover:text-[#1A1A1A] hover:bg-black/[0.04] transition-colors">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="relative" onMouseEnter={() => openDesktopDropdown('about')} onMouseLeave={closeDesktopDropdown}>
            <Link
              href="/about"
              onClick={() => setActiveDesktopDropdown(null)}
              className={`flex items-center gap-1 text-sm transition-colors ${
                activeDesktopDropdown === 'about' ? "text-[#1A1A1A]" : "text-[#1A1A1A]/50 hover:text-[#1A1A1A]"
              }`}
            >
              About
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" className="mt-px" style={{ transform: activeDesktopDropdown === 'about' ? "rotate(180deg)" : undefined, transition: "transform 0.15s" }}>
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <div className={`absolute top-full right-0 z-50 pt-2 transition-opacity duration-150 ${activeDesktopDropdown === 'about' ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
              <div className="bg-[#F4F2EF] border border-black/[0.08] rounded-lg shadow-lg py-1.5 min-w-[148px]">
                {aboutLinks.map((link) => (
                  <Link key={link.href} href={link.href} onClick={() => setActiveDesktopDropdown(null)} className="block px-4 py-2 text-sm text-[#1A1A1A]/60 hover:text-[#1A1A1A] hover:bg-black/[0.04] transition-colors">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link href="/contact" className="text-sm font-medium bg-[#FE6305] text-white px-4 py-2 rounded hover:bg-[#FE6305]/85 transition-colors">
            Contact
          </Link>
        </nav>

        <button
          type="button"
          className="ml-auto md:hidden p-2 cursor-pointer text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
        >
          {open ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M3 6H17M3 10H17M3 14H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </div>

      {activeDesktopDropdown === 'services' && (
        <div
          className="hidden md:block fixed top-[110px] right-4 sm:right-6 z-50 w-[1000px] max-w-[calc(100vw-2rem)]"
          onMouseEnter={() => openDesktopDropdown('services')}
          onMouseLeave={closeDesktopDropdown}
        >
          <div className="bg-[#F4F2EF] border border-black/[0.08] rounded-xl shadow-xl overflow-x-hidden overflow-y-auto max-h-[calc(100vh-130px)]">
            <div className="grid grid-cols-3 divide-x divide-black/[0.06]">

              {/* Technology Consulting */}
              <div className="p-5">
                <Link
                  href="/services/technology-consulting"
                  className="block text-[10px] font-semibold uppercase tracking-widest text-[#FE6305] mb-3 hover:text-[#FE6305]/70 transition-colors"
                  onClick={() => setActiveDesktopDropdown(null)}
                >
                  Technology Consulting
                </Link>
                <div>
                  {techConsultingCategories.map((cat, i) => (
                    <div key={cat.label} className={i > 0 ? "mt-4" : ""}>
                      <Link
                        href={cat.href}
                        className="block text-[9px] font-semibold uppercase tracking-widest text-[#FE6305]/70 hover:text-[#FE6305] hover:underline px-2 pb-1 transition-colors"
                        onClick={() => setActiveDesktopDropdown(null)}
                      >
                        {cat.label}
                      </Link>
                      <div className="space-y-0.5">
                        {cat.links.map((link) => (
                          <Link
                            key={link.href}
                            href={link.href}
                            className="block px-2 py-1.5 text-[13px] text-[#1A1A1A]/60 hover:text-[#1A1A1A] hover:bg-black/[0.04] rounded transition-colors"
                            onClick={() => setActiveDesktopDropdown(null)}
                          >
                            {link.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Managed Services */}
              <div className="p-5">
                <Link
                  href="/services/management"
                  className="block text-[10px] font-semibold uppercase tracking-widest text-[#FE6305] mb-3 hover:text-[#FE6305]/70 transition-colors"
                  onClick={() => setActiveDesktopDropdown(null)}
                >
                  Managed Services
                </Link>
                <div>
                  {managementCategories.map((cat, i) => (
                    <div key={cat.label} className={i > 0 ? "mt-4" : ""}>
                      <Link
                        href={cat.href}
                        className="block text-[9px] font-semibold uppercase tracking-widest text-[#FE6305]/70 hover:text-[#FE6305] hover:underline px-2 pb-1 transition-colors"
                        onClick={() => setActiveDesktopDropdown(null)}
                      >
                        {cat.label}
                      </Link>
                      <div className="space-y-0.5">
                        {cat.links.map((link) => (
                          <Link
                            key={link.href}
                            href={link.href}
                            className="block px-2 py-1.5 text-[13px] text-[#1A1A1A]/60 hover:text-[#1A1A1A] hover:bg-black/[0.04] rounded transition-colors"
                            onClick={() => setActiveDesktopDropdown(null)}
                          >
                            {link.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column 3 — Buildout + Ephemeral stacked */}
              <div className="p-5 space-y-6">
                {/* Buildout & Installation */}
                <div>
                  <Link
                    href="/services/buildout"
                    className="block text-[10px] font-semibold uppercase tracking-widest text-[#FE6305] mb-3 hover:text-[#FE6305]/70 transition-colors"
                    onClick={() => setActiveDesktopDropdown(null)}
                  >
                    Buildout &amp; Installation
                  </Link>
                  <div className="space-y-0.5">
                    {buildoutCategories[0].links.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        className="block px-2 py-1.5 text-[13px] text-[#1A1A1A]/60 hover:text-[#1A1A1A] hover:bg-black/[0.04] rounded transition-colors"
                        onClick={() => setActiveDesktopDropdown(null)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* divider */}
                <div className="border-t border-black/[0.06]" />

                {/* Ephemeral Infrastructure */}
                <div>
                  <Link
                    href="/services/ephemeral"
                    className="block text-[10px] font-semibold uppercase tracking-widest text-[#FE6305] mb-3 hover:text-[#FE6305]/70 transition-colors"
                    onClick={() => setActiveDesktopDropdown(null)}
                  >
                    Ephemeral Infrastructure
                  </Link>
                  <div className="space-y-0.5">
                    {ephemeralCategories[0].links.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        className="block px-2 py-1.5 text-[13px] text-[#1A1A1A]/60 hover:text-[#1A1A1A] hover:bg-black/[0.04] rounded transition-colors"
                        onClick={() => setActiveDesktopDropdown(null)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

</div>
          </div>
        </div>
      )}

      {/* Mobile menu */}
      <div
        id="mobile-menu"
        className="md:hidden overflow-hidden bg-[#F4F2EF]"
        style={{
          maxHeight: open ? "calc(100vh - 64px)" : "0",
          borderBottom: open ? "1px solid rgba(0,0,0,0.08)" : undefined,
          transition: "max-height 0.25s ease-in-out",
        }}
      >
        <nav
          className="px-4 py-6 flex flex-col gap-1 overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 64px)" }}
        >
          {/* Technology Consulting accordion */}
          <div className="border-b border-black/[0.08]">
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-base text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors"
              onClick={() => setTcOpen((prev) => !prev)}
            >
              Technology Consulting
              <svg
                width="14"
                height="14"
                viewBox="0 0 10 10"
                fill="none"
                aria-hidden="true"
                style={{ transform: tcOpen ? "rotate(180deg)" : undefined, transition: "transform 0.2s" }}
              >
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {tcOpen && (
              <div className="pb-2">
                <Link
                  href="/services/technology-consulting"
                  className="block py-2.5 text-sm text-[#FE6305] font-semibold border-b border-black/[0.05]"
                  onClick={() => setOpen(false)}
                >
                  Overview
                </Link>
                {techConsultingCategories.map((cat) => (
                  <div key={cat.label}>
                    <Link
                      href={cat.href}
                      className="block pt-3 pb-1 text-[9px] font-semibold uppercase tracking-widest text-[#FE6305]/70 hover:text-[#FE6305] hover:underline transition-colors"
                      onClick={() => setOpen(false)}
                    >
                      {cat.label}
                    </Link>
                    {cat.links.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        className="block py-2.5 text-sm text-[#1A1A1A]/40 pl-4 hover:text-[#1A1A1A] transition-colors border-b border-black/[0.05]"
                        onClick={() => setOpen(false)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Managed Services accordion */}
          <div className="border-b border-black/[0.08]">
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-base text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors"
              onClick={() => setMgmtOpen((prev) => !prev)}
            >
              Managed Services
              <svg
                width="14"
                height="14"
                viewBox="0 0 10 10"
                fill="none"
                aria-hidden="true"
                style={{ transform: mgmtOpen ? "rotate(180deg)" : undefined, transition: "transform 0.2s" }}
              >
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {mgmtOpen && (
              <div className="pb-2">
                <Link
                  href="/services/management"
                  className="block py-2.5 text-sm text-[#FE6305] font-semibold border-b border-black/[0.05]"
                  onClick={() => setOpen(false)}
                >
                  Overview
                </Link>
                {managementCategories.map((cat) => (
                  <div key={cat.label}>
                    <Link
                      href={cat.href}
                      className="block pt-3 pb-1 text-[9px] font-semibold uppercase tracking-widest text-[#FE6305]/70 hover:text-[#FE6305] hover:underline transition-colors"
                      onClick={() => setOpen(false)}
                    >
                      {cat.label}
                    </Link>
                    {cat.links.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        className="block py-2.5 text-sm text-[#1A1A1A]/40 pl-4 hover:text-[#1A1A1A] transition-colors border-b border-black/[0.05]"
                        onClick={() => setOpen(false)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Buildout & Installation accordion */}
          <div className="border-b border-black/[0.08]">
            <Link
              href="/services/buildout"
              className="w-full flex items-center justify-between py-3 text-base text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors"
              onClick={() => setOpen(false)}
            >
              Buildout &amp; Installation
            </Link>
          </div>

          {/* Ephemeral Infrastructure accordion */}
          <div className="border-b border-black/[0.08]">
            <Link
              href="/services/ephemeral"
              className="w-full flex items-center justify-between py-3 text-base text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors"
              onClick={() => setOpen(false)}
            >
              Ephemeral Infrastructure
            </Link>
          </div>

          {/* Our Work accordion */}
          <div className="border-b border-black/[0.08]">
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-base text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors"
              onClick={() => setOurWorkOpen((prev) => !prev)}
            >
              Our Work
              <svg
                width="14"
                height="14"
                viewBox="0 0 10 10"
                fill="none"
                aria-hidden="true"
                style={{ transform: ourWorkOpen ? "rotate(180deg)" : undefined, transition: "transform 0.2s" }}
              >
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {ourWorkOpen && (
              <div className="pb-2">
                <Link
                  href="/our-work"
                  className="block py-2.5 text-sm text-[#FE6305] font-semibold border-b border-black/[0.05]"
                  onClick={() => setOpen(false)}
                >
                  Overview
                </Link>
                {ourWorkLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="block py-2.5 text-sm text-[#1A1A1A]/40 pl-4 hover:text-[#1A1A1A] transition-colors border-b border-black/[0.05]"
                    onClick={() => setOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Pricing link */}
          <div className="border-b border-black/[0.08]">
            <Link
              href="/pricing"
              className="block py-3 text-base text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors"
              onClick={() => setOpen(false)}
            >
              Pricing
            </Link>
          </div>

          {/* Resources accordion */}
          <div className="border-b border-black/[0.08]">
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-base text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors"
              onClick={() => setResourcesOpen((prev) => !prev)}
            >
              Resources
              <svg
                width="14"
                height="14"
                viewBox="0 0 10 10"
                fill="none"
                aria-hidden="true"
                style={{ transform: resourcesOpen ? "rotate(180deg)" : undefined, transition: "transform 0.2s" }}
              >
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {resourcesOpen && (
              <div className="pb-2">
                <Link
                  href="/resources"
                  className="block py-2.5 text-sm text-[#FE6305] font-semibold border-b border-black/[0.05]"
                  onClick={() => setOpen(false)}
                >
                  Overview
                </Link>
                {resourcesLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="block py-2.5 text-sm text-[#1A1A1A]/40 pl-4 hover:text-[#1A1A1A] transition-colors border-b border-black/[0.05]"
                    onClick={() => setOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* About accordion */}
          <div className="border-b border-black/[0.08]">
            <button
              type="button"
              className="w-full flex items-center justify-between py-3 text-base text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors"
              onClick={() => setAboutOpen((prev) => !prev)}
            >
              About
              <svg
                width="14"
                height="14"
                viewBox="0 0 10 10"
                fill="none"
                aria-hidden="true"
                style={{ transform: aboutOpen ? "rotate(180deg)" : undefined, transition: "transform 0.2s" }}
              >
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {aboutOpen && (
              <div className="pb-2">
                {aboutLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="block py-2.5 text-sm text-[#1A1A1A]/40 pl-4 hover:text-[#1A1A1A] transition-colors border-b border-black/[0.05]"
                    onClick={() => setOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            href="/contact"
            className="mt-4 text-center text-sm font-medium bg-[#FE6305] text-white px-4 py-3 rounded hover:bg-[#FE6305]/85 transition-colors"
            onClick={() => setOpen(false)}
          >
            Contact
          </Link>
        </nav>
      </div>
    </header>
  );
}
